import { signal } from '@angular/core';
import { FilterKeywords } from '@common/components/filter/filter-keywords';
import { DateRange } from '@common/data-models/complex-types/date-range';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { OPTION_ALL, OPTION_ANY, OPTION_BLANK, OPTION_ME, OPTION_NO, OPTION_YES, PUBLICATION_LIST_TIME_OPTIONS, PUBLICATION_LIST_TIME_TODAY } from '@common/enums/constants';
import { IDeepCopy } from '@common/interfaces/i-deep-copy';
import { LanguageSettings } from '@common/services/language-settings';
import { Util } from '@common/utils/util';
import { Record } from '@src/suite/common/data-models/records/record';

export class FilterDef implements IDeepCopy<FilterDef> {
    private readonly allOption = 'All';
    private readonly yesOption = 'Yes';
    private readonly noOption = 'No';

    private prev: any = signal(null);
    private value: any = signal(null);

    public get Value() {
        return this.value();
    }
    public set Value(value: any) {
        if (value !== this.value()) {
            this.prev.set(this.value());
            this.value.set(value);
        }
    }

    public IsBlank: boolean = false;

    public Options: Array<any> = [];
    public OnChange: (value: any, prev: any) => any;

    public IsString: boolean = false;
    public IsDateRange: boolean = false;
    public IsDateTimeRange: boolean = false;
    public IsCheckList: boolean = false;
    public IsComboBox: boolean = false;
    public IsCheckBox: boolean = false;
    public IsSelect: boolean = false;
    public IsTree: boolean = false;
    public IsCheckListTree: boolean = false;
    public IsStaffIDList: boolean = false;
    public IsLookupList: boolean = false;
    public IsExactDate: boolean = false;

    public static LanguageSettings: LanguageSettings;

    public IsNegation: boolean = false;
    public PSTField: string = '';
    public IsRelationship: boolean = false;

    public IsRequired: boolean = true;
    public IsOptional: boolean = false;
    public NotUsed: boolean = false;
    public ID: string;
    public DefaultCaption: string;
    public Validation: string;

    public isValueSet(): boolean {
        if (this.Value === OPTION_ALL || (this.Value && this.Value.length && this.Value.length === 1 && this.Value[0] === OPTION_ALL)) {
            return false;
        }
        if ((this.IsLookupList || this.IsStaffIDList || this.IsCheckList || this.IsCheckListTree) && this.Options && this.Options.length === 0) {
            return false;
        }
        if (this.IsString && this.Value === '') {
            return false;
        }
        if (this.IsExactDate && !this.Value) {
            return false;
        }
        if ((this.IsDateRange || this.IsDateTimeRange) && (this.Value._custom === OPTION_ALL || this.Value._custom === 'ALL')) {
            return false;
        }
        if (this.IsSelect && !this.Value) {
            return false;
        }
        if (
            !this.IsString &&
            !this.IsDateRange &&
            !this.IsDateTimeRange &&
            !this.IsCheckList &&
            !this.IsComboBox &&
            !this.IsCheckBox &&
            !this.IsTree &&
            !this.IsCheckListTree &&
            !this.IsStaffIDList &&
            !this.IsLookupList &&
            !this.IsExactDate &&
            !this.IsSelect
        ) {
            return false;
        }

        if (this.Value) {
            return true;
        }

        return false;
    }

    public get IsDirty(): boolean {
        if (this.Value) {
            if (this.IsDateRange || this.IsDateTimeRange) {
                return !!(<DateRange>this.Value).to || !!(<DateRange>this.Value).from || !!(<DateRange>this.Value).custom;
            }
            if (this.IsCheckList || this.IsComboBox || this.IsCheckListTree) {
                return Array<any>(this.Value).length > 0;
            }
            return true;
        }
        return false;
    }

    constructor(fieldDef?: FieldDef, filterValue?: any) {
        if (fieldDef) {
            this.ID = fieldDef.ID;
            this.DefaultCaption = fieldDef.DefaultCaption;
            this.Validation = fieldDef.Validation.toUpperCase();
            this.init();
        }
        if (filterValue) {
            this.Value = filterValue;
        }
    }

    public group(): any {
        const FieldName: string = `${this.ID}__group__`;
        return [
            { FieldName, FieldValue: this.Value },
            { FieldName, FieldValue: `${this.ID}.${this.Value}` },
        ];
    }

    private getRelationship(): any {
        if (!this.IsCheckList && !this.IsComboBox) {
            return null;
        }
        let name: string = this.PSTField + (this.IsNegation ? '__negrellist__' : '__rellist__');
        name = this.encodeRequired(name);
        const value = <Array<string>>this.Value;
        const val: Array<string> = new Array<string>();
        let GetAll: boolean = false;
        let GetAny: boolean = false;
        for (const v of value) {
            if (v === OPTION_ALL) {
                GetAll = true;
                break;
            }
            if (v === OPTION_ANY) {
                GetAny = true;
            }
            val.push(v);
        }
        if (GetAll) {
            return null;
        }
        if (GetAny) {
            return {
                FieldName: name,
                FieldValue: val
                    .concat(
                        this.Options.map(item => {
                            if (item.id !== OPTION_ALL && item.id !== OPTION_ANY && item.id !== OPTION_BLANK) {
                                return item.id;
                            }
                            return '';
                        })
                    )
                    .join(';'),
            };
        }
        return { FieldName: name, FieldValue: val.join(';') };
    }

    private encodeRequired(name: string): string {
        return name + (!this.IsRequired ? '[r0]' : '');
    }

    public get(filter?: any): any {
        if (this.IsRelationship) {
            return this.getRelationship();
        }
        if (this.IsString) {
            const FieldName: string = `${this.ID}__like__`;
            let FieldValue: string = '';
            if (this.IsBlank) FieldValue = OPTION_BLANK;
            if (this.Value) {
                if (FieldValue) FieldValue += ';';
                FieldValue += this.Value;
            }
            if (FieldValue) return { FieldName: this.encodeRequired(FieldName), FieldValue };
        }
        if (this.IsDateRange || this.IsDateTimeRange) {
            const value = <DateRange>this.Value;
            if (value && value.valid()) {
                const FieldName: string = `${this.ID}__range__`;
                const Range: [Date, Date] = value.get();

                if (Range) {
                    if (!Range[0]) Range[0] = new Date(0);
                    if (!Range[1]) Range[1] = new Date(3000, 1);

                    return { FieldName: this.encodeRequired(FieldName), FieldValue: `${Range[0].toISOString()};${Range[1].toISOString()}` };
                }
            }
            return null;
        }
        if (this.IsCheckList || this.IsComboBox || this.IsCheckListTree) {
            const value = <Array<string>>this.Value;
            if (value && value.length > 0) {
                let GetAll: boolean = false;
                const FieldName: string = `${this.ID}__list__`;
                const FieldValue: Array<string> = [];
                for (const v of value) {
                    if (v === OPTION_ALL) {
                        GetAll = true;
                        break;
                    }
                    FieldValue.push(v);
                }
                if (!GetAll) {
                    return { FieldName: this.encodeRequired(FieldName), FieldValue: FieldValue.join(';') };
                }
            }
            return null;
        }
        if (this.IsTree && this.Value) {
            if (this.Value !== OPTION_ALL) {
                const FieldName: string = this.ID;
                const FieldValue: string = this.Value;
                return { FieldName: this.encodeRequired(FieldName), FieldValue };
            }
        }
        if (this.IsCheckBox && this.Value) {
            if (this.Value !== OPTION_ALL) {
                let FieldName: string = this.ID;
                let FieldValue: string = this.Value;
                if (FieldValue === OPTION_YES) {
                    FieldValue = 'true';
                } else {
                    FieldValue = 'false';
                }
                if (this.Value === OPTION_BLANK) {
                    FieldName = `-${FieldName}`;
                }
                return { FieldName: this.encodeRequired(FieldName), FieldValue };
            }
        }
        if (this.IsSelect && this.Value) {
            if (this.Value !== OPTION_ALL) {
                const FieldName: string = this.ID;
                const FieldValue: string = this.Value;
                return { FieldName: this.encodeRequired(FieldName), FieldValue };
            }
        }
        if (this.IsStaffIDList) {
            const FieldName: string = `${this.ID}__list__`;
            let fieldValue: string = '';
            if (this.IsBlank) fieldValue = OPTION_BLANK;

            if (this.Value) {
                if (this.Value instanceof MultiValue) {
                    if (this.Value.length > 0 || (filter && filter.length > 0)) {
                        const userIdValues = filter && filter.length > 0 ? filter : this.Value;
                        const cleanFieldValue = userIdValues
                            .map(item => {
                                if (item === OPTION_ME) {
                                    return Record.nomWebServicesJson.UserId;
                                }
                                return item;
                            })
                            .join(';');
                        const idFieldValue = userIdValues
                            .map(item => {
                                if (item === OPTION_ME) {
                                    return `${this.ID}.${Record.nomWebServicesJson.UserId}`;
                                }
                                return `${this.ID}.${item}`;
                            })
                            .join(';');
                        if (fieldValue) fieldValue += ';';
                        fieldValue += `${cleanFieldValue};${idFieldValue}`;
                    }
                } else if (Array.isArray(this.Value) && (this.Value.length > 0 || this.IsBlank)) {
                    const tmpFieldValue: Array<string> = [];
                    for (const value of this.Value) {
                        const runtimeValue = FilterKeywords.getValue(value);
                        let effectiveVal: string = typeof runtimeValue !== 'undefined' ? runtimeValue : value;
                        if (effectiveVal === OPTION_ME) effectiveVal = Record.nomWebServicesJson.UserId;
                        tmpFieldValue.push(effectiveVal);
                        tmpFieldValue.push(`${this.ID}.${effectiveVal}`);
                    }
                    if (fieldValue) fieldValue += ';';
                    fieldValue += tmpFieldValue.join(';');
                } else {
                    if (fieldValue) {
                        if (fieldValue === OPTION_ME) {
                            Record.nomWebServicesJson.UserId += ';';
                        } else {
                            fieldValue += ';';
                        }
                    }
                    if (this.Value === OPTION_ME) this.Value = Record.nomWebServicesJson.UserId;
                    fieldValue += `${this.Value};${this.ID}.${this.Value}`;
                }
            }
            if (fieldValue) return { FieldName: this.encodeRequired(FieldName), FieldValue: fieldValue };
        }
        if (this.IsLookupList) {
            const FieldName: string = `${this.ID}_DisplayName__list__`;
            const FieldValue: Array<string> = [];
            if (this.IsBlank) FieldValue.push(OPTION_BLANK);
            if (Array.isArray(this.Value) && this.Value.length > 0) {
                for (const value of this.Value) {
                    if (typeof value === 'object') {
                        FieldValue.push(value.id);
                    } else {
                        FieldValue.push(value);
                    }
                }
            }
            if (FieldValue.length > 0) {
                return { FieldName: this.encodeRequired(FieldName), FieldValue: FieldValue.join(';') };
            }
        }
        if (this.IsExactDate) {
            if (!this.Value) this.Value = PUBLICATION_LIST_TIME_TODAY;
            return { FieldName: this.ID, FieldValue: this.Value };
        }
        return null;
    }

    public init() {
        if (this.Validation === 'TEXT') {
            this.IsString = true;
            return;
        }
        if (this.ID.toUpperCase() === 'PSTPUBLICATIONLISTTIME') {
            this.IsExactDate = true;
            return;
        }
        if (['TIME', 'DATE', 'MOSDATETIME'].indexOf(this.Validation) > -1) {
            this.IsDateRange = true;
            return;
        }
        if (this.Validation === 'DATETIMERANGE') {
            this.IsDateTimeRange = true;
            return;
        }
        if (this.Validation === 'CHECKLIST') {
            this.IsCheckList = true;
            return;
        }
        if (this.Validation === 'COMBOBOX') {
            this.IsComboBox = true;
            return;
        }
        if (this.Validation === 'CHECKBOX') {
            this.IsCheckBox = true;
            return;
        }
        if (this.Validation === 'STAFFIDLIST') {
            this.IsStaffIDList = true;
            return;
        }
        if (this.Validation === 'LOOKUPLIST') {
            this.IsLookupList = true;
            return;
        }
        if (this.Validation === 'TREE') {
            this.IsCheckListTree = true;
            return;
        }
        if (this.Validation === 'SELECT') {
            this.IsSelect = true;
            return;
        }
    }

    public static create(ID: string, validation: string, value: any): FilterDef {
        const filterDef: FilterDef = new FilterDef();
        filterDef.ID = ID;
        filterDef.Validation = validation.toUpperCase();
        filterDef.Value = value;
        filterDef.init();
        return filterDef;
    }

    public get IsAllSelected(): boolean {
        if (Array.isArray(this.Value)) {
            if (this.Value.length === 1 && this.Value[0] === OPTION_ALL) {
                return true;
            }
            return false;
        }
        return this.Value === OPTION_ALL;
    }

    public toJSON() {
        return {
            ID: this.ID,
            validation: this.Validation,
            value: this.Value,
            required: this.IsRequired,
            optional: this.IsOptional,
            relationship: this.IsRelationship,
            notused: this.NotUsed,
            isblank: this.IsBlank,
        };
    }

    public initialize(value?: any) {
        if (this.IsString) {
            this.Value = value || '';
            return;
        }
        if (this.IsSelect) {
            this.Value = value || OPTION_ALL;
            return;
        }
        if (this.IsCheckList || this.IsComboBox || this.IsCheckListTree) {
            if (value) {
                if (Array.isArray(value)) {
                    let tmp = [];
                    for (let i = 0; i < value.length; i++) {
                        if (typeof value[i] === 'object') {
                            tmp.push(value[i].id);
                        } else {
                            tmp.push(value[i]);
                        }
                    }
                    if (tmp.indexOf(FilterDef.LanguageSettings.PSTWebAllOption) > -1 || tmp.indexOf(OPTION_ALL) > -1) {
                        tmp = [OPTION_ALL];
                    }
                    this.Value = tmp;
                } else if (typeof value === 'object') {
                    this.Value = [value.id];
                } else {
                    this.Value = value;
                }
            } else {
                this.Value = [OPTION_ALL];
            }
            return;
        }
        if (this.IsDateRange || this.IsDateTimeRange) {
            const daterange: DateRange = new DateRange();
            if (value) {
                daterange.from = null;
                daterange.to = null;
                if (value.custom || value._custom) {
                    daterange.custom = value.custom || value._custom;
                } else {
                    try {
                        if (value.from) {
                            daterange.from = new Date(value.from);
                        }
                        if (value.to) {
                            daterange.to = new Date(value.to);
                        }
                    } catch (e) {
                        console.log(e);
                    }
                    daterange.custom = null;
                }
            } else {
                daterange.custom = OPTION_ALL;
            }
            this.Value = daterange;
            return;
        }
        if (this.IsCheckBox) {
            let selected: string = OPTION_ALL;
            if (value) {
                if (typeof value === 'string') {
                    if (<string>value === FilterDef.LanguageSettings.PSTWebAllOption || <string>value === OPTION_ALL) {
                        selected = OPTION_ALL;
                    }
                    if (<string>value === FilterDef.LanguageSettings.PSTWebYesOption || <string>value === OPTION_YES) {
                        selected = OPTION_YES;
                    }
                    if (<string>value === FilterDef.LanguageSettings.PSTWebNoOption || <string>value === OPTION_NO) {
                        selected = OPTION_NO;
                    }
                } else {
                    // Apply value only if valid
                    const { id } = <{ id: string; title: string }>value;
                    if ([OPTION_ALL, OPTION_YES, OPTION_NO].indexOf(id) > -1) {
                        selected = id;
                    }
                }
            }

            this.Value = selected;
            return;
        }
        if (this.IsTree) {
            let selected: string = OPTION_ALL;
            if (value) {
                selected = value;
            }
            this.Value = selected;
            return;
        }
        if (this.IsStaffIDList) {
            const multiValue: MultiValue<string> = new MultiValue<string>();
            if (value) {
                if (Array.isArray(value)) {
                    for (let v of value) {
                        if (v === OPTION_ME) v = Record.nomWebServicesJson.UserId;
                        multiValue.set(v);
                    }
                }
                if (value.data) {
                    for (let v of value.data) {
                        if (v === OPTION_ME) v = Record.nomWebServicesJson.UserId;
                        multiValue.set(v);
                    }
                }
            }
            this.Value = multiValue;
        }

        if (this.IsLookupList) {
            this.Value = value || new LookupList();
        }

        if (this.IsExactDate) {
            this.Options = PUBLICATION_LIST_TIME_OPTIONS;
            this.Value = value;
        }
    }

    public adjust(value: any) {
        if (this.IsCheckList || this.IsComboBox || this.IsTree || this.IsCheckListTree) {
            if (value.state.length === 0 || value.id === OPTION_ALL) {
                this.Value = [OPTION_ALL];
            } else {
                this.Value = value.state.filter((id: string) => id !== OPTION_ALL);
            }
        }

        if (this.IsSelect) {
            this.Value = value;
        }

        if (this.IsLookupList || this.IsCheckBox || this.IsString || this.IsExactDate) {
            this.Value = value;
        }
    }

    public onChanged() {
        this.OnChange(this.value, this.prev);
    }

    public deepCopy(): FilterDef {
        const ret = FilterDef.create(this.ID, this.Validation, this.Value);
        ret.PSTField = this.PSTField;
        ret.IsNegation = this.IsNegation;
        ret.IsRelationship = this.IsRelationship;
        ret.Value = Util.deepCopy(this.Value);
        ret.IsBlank = this.IsBlank;
        ret.DefaultCaption = this.DefaultCaption;
        ret.Options = Util.deepCopy(this.Options);
        ret.IsRequired = this.IsRequired;
        ret.IsOptional = this.IsOptional;
        ret.NotUsed = this.NotUsed;
        if (ret.IsRelationship) {
            ret.init();
        }
        return ret;
    }
}
